import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { useTheme } from "@mui/material/styles";
import AutocompleteWithQuery from "components/AutocompleteWithQuery";
import Field from "components/Field";
import FieldArray from "components/FieldArray";
import FieldRichText from "components/FieldRichText";
import SnackbarMessage from "components/SnackbarMessage";
import RegisterPlugins from "components/TinyMCEPlugins/Plugins/RegisterPlugins";
import { FORM_ERROR } from "final-form";
import arrayMutators from "final-form-arrays";
import { Guid } from "guid-typescript";

import {
  canHaveAnswers,
  canHaveConfidenceLevels,
  canHaveFeedback,
  isMultipleChoiceOrAnswersQuestionType,
} from "helpers/QuestionTypeHelpers";
import { isImportedQuestion } from "utils/evaluationQuestionUtils";
import { makeStyles } from "helpers/StyleHelper";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { omit, pick, sortBy } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  AdminListRequestOfQuestionTypeFilter,
  AdminListRequestOfRatingSettingFilter,
  EvaluationQuestionAnswer,
  IEvaluationQuestionAnswer,
  IQuestion,
  ISaveQuestion,
  ListResponseOfQuestionType,
  ListResponseOfRatingSetting,
  QuestionType,
  ResponseOfBoolean,
} from "services/KnowfullyAdminClient";
import { Editor } from "tinymce";
import {
  Mode,
  QuestionTypeEnum,
  SnackbarMessageProps,
} from "types/common-types";
import OrderableAnswers from "./OrderableAnswers";

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 15px",
  },
  confidenceLevel: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  checkboxesRow: {
    display: "flex",
  },
  subtitle: {
    margin: theme.spacing(3, 0, 2),
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  dialogContent: {
    padding: "40px",
    borderBottom: "unset",
  },
  createDialogActions: {
    display: "flex",
    justifyContent: "space-between",
    padding: "0 40px 40px 40px",
  },
  createAnotherQuestion: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
  },
  createQuestionButton: {
    marginLeft: "5px",
  },
}));

type QuestionFormValues = Omit<
  IQuestion,
  "questionType" | "ratingSetting" | "answers"
> & {
  questionTypeId?: string;
  ratingSettingId?: string;
  answers?: IEvaluationQuestionAnswer[];
};

const { Form } = withTypes<QuestionFormValues>();

export type QuestionFormSubmitValues = Omit<ISaveQuestion, "organizationId">;

type QuestionFormProps = {
  question?: IQuestion;
  submitLabel: string;
  onSubmit: (
    values: QuestionFormSubmitValues,
    createAnother?: boolean
  ) => void;
  onCancel?: () => void;
  mode: Mode;
};

const getProvisionalId = () => `new_${Guid.raw()}`;

const QuestionForm = ({
  question,
  submitLabel,
  onCancel,
  onSubmit,
  mode,
}: QuestionFormProps) => {
  const isUpdate = mode === Mode.Update;
  const isCreate = mode === Mode.Create;
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const { questionService } = useGlobalContext();
  const [createAnotherQuestion, createAnotherQuestionActions] = useBoolean(
    false
  );
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const questionHasId = !!question?.id && !isImportedQuestion(question);
  const isQuestionEditableResponse = useQuery<ResponseOfBoolean>(
    ["isQuestionEditable", question?.id],
    () => questionService.isQuestionEditable(question!.id!),
    { enabled: questionHasId }
  );

  const fetchedQuestionTypes = useQuery<ListResponseOfQuestionType>(
    "questionTypes",
    () =>
      questionService.getQuestionTypes(
        new AdminListRequestOfQuestionTypeFilter({ sortField: "name" })
      )
  );

  const fetchedRatingSettings = useQuery<ListResponseOfRatingSetting>(
    "questionRatings",
    () =>
      questionService.getRatingSettings(
        new AdminListRequestOfRatingSettingFilter({ sortField: "name" })
      )
  );

  const init = useMemo(() => {
    const orderedAnswers = sortBy(
      question?.evaluationQuestionAnswers ?? [],
      "sequence"
    );
    const initialAnswers = orderedAnswers.length
      ? orderedAnswers
      : [
          {
            id: getProvisionalId(),
            isAnswer: true,
            value: "",
          },
        ];
    return {
      required: question?.required ?? true,
      deleted: false,
      sequence: 0,
      answers: initialAnswers,
      ...pick(question ?? {}, [
        "value",
        "required",
        "deleted",
        "hasConfidenceLevels",
        "questionFeedback",
        "questionFeedbackId",
      ]),
      questionTypeId: question?.questionType?.id,
      ratingSettingId: question?.ratingSetting?.id,
    };
  }, [question]);

  const innerOnSubmit = useCallback(
    (values: QuestionFormValues) => {
      try {
        const questionType = fetchedQuestionTypes.data!.data!.find(
          (qt) => qt.id === values.questionTypeId
        ) as QuestionType;

        let answers: EvaluationQuestionAnswer[] = [];

        if (canHaveAnswers(questionType)) {
          answers = values
            .answers!.filter((x) => x.value)
            .map(
              (a, i) =>
                new EvaluationQuestionAnswer({
                  ...a,
                  isAnswer:
                    questionType.value === QuestionTypeEnum.TextQuestion &&
                    !a?.value
                      ? false
                      : a.isAnswer,
                  // We remove any provisional Ids that we created
                  id: a.id?.startsWith("new_") ? undefined : a.id,
                  caseSensitive: true,
                  sequence: i,
                })
            );

          if (
            questionType.value !== QuestionTypeEnum.TextQuestion &&
            !answers.length
          ) {
            return {
              [FORM_ERROR]: t({ id: "answersRequired" }),
            };
          }
        }

        return onSubmit(
          {
            ...omit(values, ["answers"]),
            ratingSettingId: values.ratingSettingId!,
            questionTypeId: values.questionTypeId!,
            answers: answers,
          },
          createAnotherQuestion
        );
      } catch (err: any) {
        console.log(err);
      }
    },
    [fetchedQuestionTypes.data, onSubmit, createAnotherQuestion, t]
  );

  const validateHTML = useCallback(
    function validateHTML(ed: Editor) {
      const str = ed.getContent();
      const hasHtmlTags = str.includes("&lt;") && str.includes("&gt;");
      if (hasHtmlTags) {
        ed.notificationManager.open({
          text: t({ id: "htmlWarningMessage" }),
          type: "warning",
        });
      }
    },
    [t]
  );

  const questionEditingDisabled =
    questionHasId && !(isQuestionEditableResponse?.data?.data ?? false);
  const showQuestionCannotBeEditedWarning =
    isQuestionEditableResponse?.data?.data === false;

  return useMemo(
    () => (
      <Form
        onSubmit={innerOnSubmit}
        initialValues={init}
        mutators={{ ...arrayMutators }}
        render={({
          form: {
            mutators: { push },
          },
          handleSubmit,
          submitting,
          submitError,
          values,
        }) => {
          const questionType = fetchedQuestionTypes.data?.data?.find(
            (qt) => qt.id === values.questionTypeId
          );

          const canAddAnswers = (type: QuestionType | undefined) => {
            return (
              isCreate &&
              type?.value &&
              isMultipleChoiceOrAnswersQuestionType(type.value)
            );
          };

          const addAnswer = canAddAnswers(questionType)
            ? () => {
                const newAnswerId = getProvisionalId();

                push("answers", {
                  id: newAnswerId,
                  isAnswer: false,
                  value: "",
                });

                return newAnswerId;
              }
            : undefined;

          return (
            <form onSubmit={handleSubmit}>
              <DialogContent dividers sx={styles.dialogContent}>
                <Grid container spacing={2}>
                  {showQuestionCannotBeEditedWarning && (
                    <Alert sx={styles.feedback} severity="warning">
                      {t({ id: "questionCannotBeEdited" })}
                    </Alert>
                  )}
                  <Field name="value" required>
                    {({ input, meta }) => {
                      return (
                        <Grid item xs={12} data-cy="questionText">
                          <FieldRichText
                            input={input}
                            meta={meta}
                            label={t({ id: "questionLabel" })}
                            init={{
                              height: 200,
                              paste_data_images: true,
                              setup: (ed) => {
                                RegisterPlugins({ editor: ed });
                                ed.on("keyup", (e) => {
                                  validateHTML(ed);
                                });
                              },
                            }}
                            disabled={questionEditingDisabled}
                          />
                        </Grid>
                      );
                    }}
                  </Field>
                  <Grid item xs={12} sx={styles.checkboxesRow}>
                    <Field name="required" type="checkbox">
                      {({ input }) => (
                        <Grid item xs={12}>
                          <Box sx={styles.checkboxContainer}>
                            <FormLabel>
                              <T id="questionsRequiredLabel" />
                            </FormLabel>
                            <Checkbox
                              inputProps={{}}
                              {...input}
                              data-cy="requiredCheckBox"
                              disabled={questionEditingDisabled}
                            />
                          </Box>
                        </Grid>
                      )}
                    </Field>

                    {canHaveConfidenceLevels(questionType) && (
                      <Field name="hasConfidenceLevels" type="checkbox">
                        {({ input }) => (
                          <Grid item xs={12}>
                            <Box sx={styles.confidenceLevel}>
                              <FormLabel>
                                <T id="addConfidenceLevelsLabel" />
                              </FormLabel>
                              <Checkbox
                                {...input}
                                data-cy="addConfidenceLevelCheckBox"
                                disabled={questionEditingDisabled}
                              />

                              <Tooltip
                                title={t({
                                  id: "evaluationConfidenceLevelsAdviceMessage",
                                })}
                              >
                                <InfoIcon />
                              </Tooltip>
                            </Box>
                          </Grid>
                        )}
                      </Field>
                    )}
                  </Grid>

                  <Field name="questionTypeId" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <AutocompleteWithQuery
                            {...input}
                            labelField="name"
                            fetchedData={fetchedQuestionTypes}
                            failMessage={t({
                              id: "selectQuestionTypeFetchFailed",
                            })}
                            label={t({ id: "questionsTypeLabel" })}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                              required: true,
                            }}
                            disabled={isUpdate || questionEditingDisabled}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  {questionType?.value === QuestionTypeEnum.Rating && (
                    <Field name="ratingSettingId" required>
                      {({ input, meta }) => (
                        <Grid item xs={12}>
                          <FormControl
                            fullWidth
                            error={meta.error && meta.submitFailed}
                          >
                            <AutocompleteWithQuery
                              {...input}
                              labelField="name"
                              fetchedData={fetchedRatingSettings}
                              failMessage={t({
                                id: "selectRatingSettingFetchFailed",
                              })}
                              label={t({ id: "questionsRatingSettingLabel" })}
                              textFieldProps={{
                                error: meta.error && meta.submitFailed,
                              }}
                              disabled={isUpdate || questionEditingDisabled}
                            />
                            {meta.error && meta.submitFailed && (
                              <FormHelperText>{meta.error}</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                      )}
                    </Field>
                  )}

                  {canHaveAnswers(questionType) && (
                    <Grid item xs={12}>
                      <Box mt={2}>
                        <Typography variant="subtitle2">
                          <T
                            id={
                              questionType?.value &&
                              isMultipleChoiceOrAnswersQuestionType(
                                questionType.value
                              )
                                ? "answersLabel"
                                : "expectedAnswerLabel"
                            }
                          />
                        </Typography>
                      </Box>
                      <FieldArray name="answers">
                        {({ fields }) => (
                          <FormControl fullWidth>
                            <Box mt={3} />
                            <OrderableAnswers
                              fields={fields}
                              mode={mode}
                              questionType={questionType?.value!}
                              addAnswer={addAnswer}
                              disabled={questionEditingDisabled}
                            />
                          </FormControl>
                        )}
                      </FieldArray>
                    </Grid>
                  )}

                  {canHaveFeedback(questionType) && (
                    <Field name="questionFeedback">
                      {({ input, meta }) => {
                        return (
                          <Grid item xs={12}>
                            <FieldRichText
                              input={input}
                              meta={meta}
                              label={t({ id: "questionFeedbackLabel" })}
                              init={{
                                height: 200,
                                paste_data_images: true,
                                setup: (ed) => {
                                  RegisterPlugins({ editor: ed });
                                  ed.on("keyup", (e) => {
                                    validateHTML(ed);
                                  });
                                },
                              }}
                            />
                          </Grid>
                        );
                      }}
                    </Field>
                  )}

                  {submitError && (
                    <Alert sx={styles.feedback} severity="error">
                      {submitError}
                    </Alert>
                  )}
                </Grid>
              </DialogContent>

              <DialogActions
                sx={mode === Mode.Create ? styles.createDialogActions : {}}
              >
                {mode === Mode.Create && (
                  <Box
                    sx={styles.createAnotherQuestion}
                    onClick={() => createAnotherQuestionActions.toggle()}
                  >
                    <Typography>
                      <T id="createAnotherQuestion" />
                    </Typography>
                    <Checkbox checked={createAnotherQuestion} />
                  </Box>
                )}
                <Box>
                  <Button
                    disabled={submitting}
                    onClick={onCancel}
                    data-cy="cancelButton"
                  >
                    {t({ id: "cancelButton" })}
                  </Button>

                  <Button
                    sx={styles.createQuestionButton}
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={submitting}
                    data-cy="submitButton"
                  >
                    {submitLabel}
                  </Button>
                </Box>
              </DialogActions>
              <SnackbarMessage
                snackbarMessage={snackbarMessage}
                setSnackbarMessage={setSnackbarMessage}
              />
            </form>
          );
        }}
      />
    ),
    [
      innerOnSubmit,
      init,
      fetchedQuestionTypes,
      styles.dialogContent,
      styles.feedback,
      styles.checkboxesRow,
      styles.createDialogActions,
      styles.createAnotherQuestion,
      styles.createQuestionButton,
      styles.checkboxContainer,
      styles.confidenceLevel,
      showQuestionCannotBeEditedWarning,
      t,
      mode,
      createAnotherQuestion,
      onCancel,
      submitLabel,
      snackbarMessage,
      isCreate,
      questionEditingDisabled,
      validateHTML,
      isUpdate,
      fetchedRatingSettings,
      createAnotherQuestionActions,
    ]
  );
};

export default QuestionForm;
